import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveAssistantStepsApi } from '../utils/apis';
import { useSelector } from 'react-redux';
import { RootState } from '../utils/redux/redux';
import { ISaveAssistantSteps } from '../schemas/apis/interface';
import { ICallStage } from '../schemas/apis/interface';

interface SaveAssistantParams {
  userId: string;
  status: 'draft' | 'complete';
  assistantId: string;
  businessId?: string;
  section:
    | 'restaurant-details'
    | 'assistant-definition'
    | 'menu-configuration'
    | 'personalization'
    | 'analytics'
    | 'reservation-system-setup'
    | 'faq-setup';
  callStages?: ICallStage[];
}

export const useSaveAssistant = () => {
  const { assistantDefinition, menuConfig, personalization, callStages } =
    useSelector((state: RootState) => state.userJourney);
  const { faqSetup } = useSelector((state: RootState) => state.generalSupport);

  const { reservationSystemSetup } = useSelector(
    (state: RootState) => state.reservation
  );

  const queryClient = useQueryClient();

  const { mutateAsync: saveAssistant, isPending } = useMutation({
    mutationFn: (params: SaveAssistantParams) => {
      let payload: Partial<ISaveAssistantSteps> = {
        assistantId: params.assistantId,
        userId: params.userId,
        status: params.status,
      };

      // Add section-specific data
      switch (params.section) {
        case 'assistant-definition': {
          // Parse language and accent from the format "Language (REGION)"
          const [languageName, regionPart] =
            assistantDefinition.data.language.split(' (');
          const accent = regionPart
            ?.replace(')', '')
            .replace('USA', 'American')
            .replace('UK', 'British')
            .replace('UAE', 'Emirati');

          payload = {
            ...payload,
            assistantName: assistantDefinition.data.assistantName,
            config: {
              personality: assistantDefinition.data.personality,
              timeZone: assistantDefinition.data.timezone,
              voice: {
                voiceName: assistantDefinition.data.voice.name,
                voiceId: assistantDefinition.data.voice._id,
                voiceProvider: assistantDefinition.data.voice.provider,
                isSampleVoice: true,
              },
              language: {
                name: languageName,
                value: accent?.toLowerCase() || '',
              },
            },
          };
          break;
        }

        case 'menu-configuration': {
          // Create a filtered menuSettingDetails based on POS name
          let filteredMenuSettingDetails;

          if (menuConfig.data.posName === 'Urban Piper') {
            // For Urban Piper, only include storeId and refId
            filteredMenuSettingDetails = Object.values(
              menuConfig.data.menuSettingDetails || {}
            ).map(item => ({
              storeId: item.storeId,
              refId: item.refId,
            }));
          } else if (menuConfig.data.posName === 'tmbill') {
            // For TMBill, only include storeId and tmBillBrandShortName
            filteredMenuSettingDetails = Object.values(
              menuConfig.data.menuSettingDetails || {}
            ).map(item => ({
              storeId: item.storeId,
              tmBillBrandShortName: item.tmBillBrandShortName,
            }));
          } else {
            // Default case
            filteredMenuSettingDetails = menuConfig.data.menuSettingDetails;
          }

          // Get posConfig data either from Redux state or look it up if not available
          let posConfigData;
          if (menuConfig.data.posConfig) {
            // Use the data already stored in Redux
            posConfigData = menuConfig.data.posConfig;
          } else {
            // Look up the data from posConfig using environment
            const environment = menuConfig.data.environment || 'stage';
            const posConfigQuery = queryClient.getQueryData(['pos-config']);
            // @ts-expect-error
            const selectedPos = posConfigQuery?.data?.data?.find(
              (pos: any) => pos.posName === menuConfig.data.posName
            );
            const envConfig = selectedPos?.pos_config[0]?.[environment]?.[0];

            if (envConfig) {
              posConfigData = {
                apikey: envConfig.apikey,
                endpoint: envConfig.endpoint,
              };
            }
          }

          payload = {
            ...payload,
            menuConfig: {
              menuSettingMethod:
                menuConfig.data.menuSettingMethod === 'pos'
                  ? 'pos_integration'
                  : menuConfig.data.menuSettingMethod === 'file_upload'
                    ? 'file_upload'
                    : menuConfig.data.menuSettingMethod === 'manual_cataloging'
                      ? 'manual_cataloging'
                      : menuConfig.data.menuSettingMethod === 'web_scraping'
                        ? 'web_scrapping'
                        : 'pos_integration',
              menuSettingDetails: filteredMenuSettingDetails as any,
              assistantId: params.assistantId,
              // Include posConfig data if available
              ...(posConfigData && {
                pos_config: {
                  apikey: posConfigData.apikey,
                  endpoint: posConfigData.endpoint,
                },
              }),
              ...(menuConfig.data.posName === 'Urban Piper' && {
                businessId: menuConfig.data.businessId,
              }),
              ...(menuConfig.data.posName === 'tmbill' && {
                tmbillposId: menuConfig.data.tmbillposId,
              }),
              posName: menuConfig.data.posName,
            },
          };
          break;
        }

        case 'personalization':
          payload = {
            ...payload,
            prompt: {
              firstMessage: personalization.data.greeting,
              identity: personalization.data.identity,
              job: personalization.data.job,
              context: '',
              examples: '',
              constraints: '',
              format: '',
              tone: '',
            },
          };
          break;

        case 'reservation-system-setup': {
          const systems = reservationSystemSetup.data.systems;
          const selectedSystem = reservationSystemSetup.data.selectedSystem;

          if (systems.length > 0) {
            // Check if it's Hotelogix
            if (selectedSystem === 'hotelogix') {
              const hotelConfigs =
                reservationSystemSetup.data.hotelConfigs || {};
              payload = {
                ...payload,
                hotelogixSetup: {
                  hotels: systems.map(system => ({
                    hotelId: system.restaurantId,
                    storeId: system.storeId,
                    storeAlias: system.storeAlias,
                    hotelConfig: hotelConfigs[system.storeId] || undefined,
                  })),
                },
              };
            } else {
              // For ReserveGo, use the existing structure
              payload = {
                ...payload,
                reservationSystemSetup: {
                  systems: systems.map(system => ({
                    restaurantId: system.restaurantId,
                    storeId: String(system.storeId),
                    reservationSystem: system.reservationSystem,
                    storeAlias: system.storeAlias,
                  })),
                },
              };
            }
          }
          break;
        }

        case 'analytics':
          payload = {
            ...payload,
            callStages: callStages?.data || [],
          };
          break;

        case 'faq-setup':
          payload = {
            ...payload,
            questionnaireData: {
              qnaArray: faqSetup.data.faqList.map(
                ({ question, answer, id }: any) => ({
                  question,
                  answer,
                  id,
                })
              ),
            },
          };
          break;
      }

      return saveAssistantStepsApi(payload).then(() => {
        queryClient.invalidateQueries({ queryKey: ['agents'] });
        queryClient.invalidateQueries({ queryKey: ['stores'] });

        // Only invalidate pos-integration-data after analytics section
        if (params.section === 'analytics') {
          queryClient.invalidateQueries({ queryKey: ['pos-integration-data'] });
        }

        queryClient.invalidateQueries({ queryKey: ['getFaqByAssistantId'] });
        queryClient.invalidateQueries({ queryKey: ['hotelogixSetup'] });
      });
    },
  });

  return {
    saveAssistant,
    isPending,
  };
};
